import { RealEstateOwnerDto, TenantMinimalDto } from "api/real-estate";
import { ReferentialKey } from "api/referential";
import { useAppDispatch } from "app/hooks";
import { GenericLoading } from "components/ui/Loader";
import { getMultipleReferentialsByKeys } from "features/referentials/slices/getMultipleReferentialsByKeysThunk";
import { FC, useCallback, useEffect, useState } from "react";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";
import { RealEstateOwnerSection } from "../tenant/RealEstateOwnerSection";
import { TenantManager } from "../tenant/TenantManager";

interface ParcBienDetailPanelProps {
  riskId: string
  bienId: string
  /** Répercute toute modification locataire sur le dossier parc (statuts, compteurs). */
  onChanged: () => void
}

/**
 * Détail d'un bien du parc (étape dossier location) : propriétaires + gestion des
 * locataires — mêmes composants que l'écran mono-bien, ouverts en panel.
 */
export const ParcBienDetailPanel: FC<ParcBienDetailPanelProps> = ({ riskId, bienId, onChanged }) => {
  const dispatch = useAppDispatch()

  const [owners, setOwners] = useState<RealEstateOwnerDto[]>([])
  const [tenants, setTenants] = useState<TenantMinimalDto[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    dispatch(getMultipleReferentialsByKeys([
      ReferentialKey.Civility,
      ReferentialKey.EmploymentStatus,
      ReferentialKey.IncomeType,
    ]))
  }, [dispatch])

  const loadTenants = useCallback(async () => {
    const response = await realEstateAPI.getTenants({ riskId, bienId })
    setTenants(response.tenants ?? [])
  }, [riskId, bienId])

  /** Rechargement après ajout/édition/sortie d'un locataire : panel + dossier parc. */
  const refreshTenants = useCallback(() => {
    loadTenants().catch(console.error)
    onChanged()
  }, [loadTenants, onChanged])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [bienResponse] = await Promise.all([
          realEstateAPI.getRealEstateById({ bienId, includeQuestionnaire: false }),
          loadTenants(),
        ])
        if (!cancelled) {
          setOwners(bienResponse.realEstate.owners ?? [])
        }
      } catch (error) {
        console.error(error)
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [bienId, loadTenants])

  if (isLoading) {
    return <GenericLoading loading />
  }

  return <div className="space-y-6">
    <RealEstateOwnerSection owners={owners} />
    <TenantManager riskId={riskId} realEstateId={bienId} tenants={tenants} loadTenants={refreshTenants} />
  </div>
}
